import { DeleteOutlined } from "@ant-design/icons"
import { Button, DatePicker, Form } from "antd"
import { FormElement, UploadComponent } from "components"
import { RichEditor } from "components/RichEditor/RichEditor"
import { ContestConfig, ContestConfigLiveData, ContestConfigSchedulePageData } from "domains/managingJson/store/types"
import moment from "moment"
import { FC, SetStateAction } from "react"
import { useStore } from "stores/utils/use-store"
import { resolvePeriodLabel } from "./resolvePeriodLabel"

interface props {
  step: (ContestConfigSchedulePageData & {
    CTAtext: string;
})
  index?: number
  deleteFunc?: () => void
  name: string
  finish?: boolean
  weeks?: ContestConfigLiveData[]
  setContestLive?: React.Dispatch<SetStateAction<ContestConfigLiveData[]>>
  setter?: React.Dispatch<SetStateAction<ContestConfigSchedulePageData>>
}

const range = (start: number, end: number): number[] => {
  const result: number[] = []

  for (let i = start; i < end; i++) result.push(i)

  return result
}

export const SchedulingImage: FC<props> = ({ step, index = -1, deleteFunc, name, finish, weeks, setContestLive, setter }) => {

  const { dataStore: { managingJsonStore: { selectedElement } } } = useStore()
  const data = selectedElement.data as ContestConfig
  const periodLabel = weeks && index > -1
    ? resolvePeriodLabel(weeks[index].periodName, index)
    : ""

  return (
    <div style={{ width: "30%", marginRight: "16px", marginBottom: "16px" }}>
      <div style={{ padding: "16px 16px 16px", border: "1px solid #F0F0F0" }}>
        <div style={{ fontWeight: "500" }}>{name}</div>
        <UploadComponent
          image={step.image}
          setImage={e => {
            if (weeks) {
              setContestLive ? setContestLive(weeks.map((w, i) => i === index ? { ...w, image: e } : w)) : ""
            } else {
              setter ? setter({ ...step, image: e }) : ""
            }
          }}
          placeholder=""
          title=""
        />
        <div style={{ background: "white", marginTop: "32px" }}>
          {weeks && (
            <div style={{ width: "100%", marginRight: "16px" }}>
              Period name
              <FormElement
                type="input"
                label=""
                name={`periodName${index + 1}`}
                initialValue={weeks[index].periodName ?? `Period ${index + 1}`}
                props={{
                  onChange: e => {
                    setContestLive
                      ? setContestLive(weeks.map((w, i) => i === index ? { ...w, periodName: e.target.value } : w))
                      : ""
                  },
                }}
              />
            </div>
          )}
          <div style={{ width: "100%", marginRight: "16px" }}>
              Title
            <FormElement
              type="input"
              label=""
              name={`weekTitle${index !== -1 ? index+1 : !finish ? "Start" : "Finish"}`}
              initialValue={step.title}
              props={{
                onChange: e => {
                  if (index !== -1 && weeks) {
                    setContestLive ? setContestLive(weeks.map((w, i) => i === index ? { ...w, title: e.target.value } : w)) : ""
                  }
                },
              }}
            />
          </div>
          <div style={{ width: "100%", marginRight: "16px" }}>
              Text
            <Form.Item
              label=""
              name={`weekText${index !== -1 ? index+1 : !finish ? "Start" : "Finish"}`}
              style={{ width: "100%" }}
              initialValue={step.text}
              valuePropName="markdown"
            >
              <RichEditor
                markdown=""
                onChange={
                  e => {
                    if (index !== -1 && weeks) {
                      setContestLive ? setContestLive(weeks.map((w, i) => i === index ? { ...w, text: e } : w)) : ""
                    }
                  }
                }
              />
            </Form.Item>
          </div>
          {finish && (
            <div style={{ width: "100%", marginRight: "16px" }}>
            CTA text
              <FormElement
                type="input"
                label=""
                name="CTAtext"
                initialValue={step.CTAtext}
              />
            </div>
          )}
          {weeks && (
            <>
              <div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span>{index === 0 ? "Contest Start" : `${periodLabel} Start`}</span>
                  {index > 0 && (
                    <Button
                      type="link"
                      size="small"
                      style={{ padding: 0, fontSize: "12px" }}
                      onClick={() => {
                        const prevEnd = moment.utc(weeks[index - 1].endDate).add(1, "s")
                        setContestLive ? setContestLive(weeks.map((w, i) => i === index ? { ...w, startDate: prevEnd.toDate() } : w)) : ""
                      }}
                    >
                      Use previous week end (+1s)
                    </Button>
                  )}
                </div>
                <DatePicker
                  format={date => date.format("YYYY-MM-DD HH:mm:ss")}
                  value={moment.utc(weeks[index].startDate)}
                  onChange={e => {
                    if (e) {
                      let val = e

                      if (index > 0) {
                        const min = moment.utc(weeks[index - 1].endDate).add(1, "s")

                        if (val.isBefore(min)) {
                          val = min.clone()
                        }
                      }

                      if (index === 0) {
                        data.startDate = val.toDate()
                      }

                      setContestLive ? setContestLive(weeks.map((w, i) => i === index ? { ...w, startDate: val.toDate() } : w)) : ""
                    }
                  }}
                  showTime
                  placeholder="Select Start Date"
                  style={{ width: "100%" }}
                  disabledDate={current => {
                    if (index > 0) {
                      const min = moment.utc(weeks[index - 1].endDate).add(1, "s")

                      return current.isBefore(min, "day")
                    }

                    return false
                  }}
                  disabledTime={current => {
                    if (index <= 0 || !current) return {}

                    const min = moment.utc(weeks[index - 1].endDate).add(1, "s")

                    if (!current.isSame(min, "day")) return {}

                    const mH = min.hour()
                    const mM = min.minute()
                    const mS = min.second()

                    return {
                      disabledHours: () => range(0, mH),
                      disabledMinutes: (h: number) => h === mH ? range(0, mM) : [],
                      disabledSeconds: (h: number, m: number) => h === mH && m === mM ? range(0, mS) : [],
                    }
                  }}
                />
              </div>
              <div>
                {index + 1 === weeks.length ? "Contest End" : `${periodLabel} End`}
                <DatePicker
                  format={date => date.format("YYYY-MM-DD HH:mm:ss")}
                  value={moment.utc(weeks[index].endDate)}
                  onChange={e => {
                    if (e) {
                      let val = e
                      const min = moment.utc(weeks[index].startDate)

                      if (val.isBefore(min)) {
                        val = min.clone()
                      }

                      if (index + 1 === weeks.length) {
                        data.endDate = val.toDate()
                      }

                      setContestLive ? setContestLive(weeks.map((w, i) => i === index ? { ...w, endDate: val.toDate() } : w)) : ""
                    }
                  }}
                  showTime
                  placeholder="Select End Date"
                  style={{ width: "100%" }}
                  disabledDate={current => {
                    return current.isBefore(moment.utc(weeks[index].startDate), "day")
                  }}
                  disabledTime={current => {
                    if (!current) return {}

                    const min = moment.utc(weeks[index].startDate)

                    if (!current.isSame(min, "day")) return {}

                    const mH = min.hour()
                    const mM = min.minute()
                    const mS = min.second()

                    return {
                      disabledHours: () => range(0, mH),
                      disabledMinutes: (h: number) => h === mH ? range(0, mM) : [],
                      disabledSeconds: (h: number, m: number) => h === mH && m === mM ? range(0, mS) : [],
                    }
                  }}
                />
              </div>
            </>
          )}
        </div>
      </div>
      {deleteFunc && (
        <div
          style={{
            margin: "",
            color: "#FF0000",
            border: "1px dotted  #D9D9D9",
            height: "50px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            cursor: "pointer",
          }}
          onClick={deleteFunc}
        ><DeleteOutlined style={{}} />Delete
        </div>
      )}
    </div>
  )
}
